import React, { useEffect, useState } from 'react';
import { Link, useParams, useHistory } from 'react-router-dom';
import {
  Container,
  Header,
  Segment,
  Form,
  Button,
  Message,
  Loader,
  Icon,
  Grid,
  TextArea,
  Label,
  Confirm,
  Modal,
  Checkbox,
  Dropdown,
} from 'semantic-ui-react';
import { Helmet } from '@plone/volto/helpers';
import { getApiPath, getAuthHeaders, fetchApi } from '../api';

// Auto-increment patch version for new drafts (BUG-2026-010)
function bumpPatch(version) {
  if (!version) return '0.0.1';
  const parts = version.split('.');
  if (parts.length === 1) return `${parts[0]}.0.1`;
  if (parts.length === 2) return `${parts[0]}.${parts[1]}.1`;
  parts[parts.length - 1] = String(Number(parts[parts.length - 1]) + 1);
  return parts.join('.');
}

const DRAFT_STATUSES = {
  draft: { color: 'blue', label: 'Draft', icon: 'edit' },
  in_review: { color: 'orange', label: 'In Review', icon: 'clock' },
  rejected: { color: 'red', label: 'Rejected', icon: 'exclamation triangle' },
};

const CLASSIFICATION_OPTIONS = [
  { key: 'MAJOR', value: 'MAJOR', text: 'MAJOR — new version required' },
  { key: 'MINOR', value: 'MINOR', text: 'MINOR — additive change' },
  { key: 'PATCH', value: 'PATCH', text: 'PATCH — editorial/correction' },
];

export default function DocEditor() {
  const params = useParams();
  const history = useHistory();
  const slug = params.slug || params[0] || '';
  const [doc, setDoc] = useState(null);
  const [draft, setDraft] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [abandonConfirm, setAbandonConfirm] = useState(false);
  const [submitModalOpen, setSubmitModalOpen] = useState(false);

  // Form fields
  const [title, setTitle] = useState('');
  const [bodyMd, setBodyMd] = useState('');
  const [author, setAuthor] = useState('');
  const [version, setVersion] = useState('');

  // CR-2026-026: Justification fields (shown on new draft for QMS docs)
  const [justWhat, setJustWhat] = useState('');
  const [justWhy, setJustWhy] = useState('');
  const [justScope, setJustScope] = useState('');
  const [justClassification, setJustClassification] = useState('PATCH');

  // CR-2026-026: COI + Qualification fields (shown on submit for QMS docs)
  const [coiNoConflicts, setCoiNoConflicts] = useState(false);
  const [coiDeclaration, setCoiDeclaration] = useState(
    'I declare that I have no financial, personal, or professional conflicts of interest that could influence the content of this document.',
  );
  const [coiMicroEnterprise, setCoiMicroEnterprise] = useState(false);
  const [qualBasis, setQualBasis] = useState('');

  // Whether current doc is QMS-registered
  const isQms = !!doc?.qms_document_id;

  // Load document and any existing draft
  useEffect(() => {
    if (!slug) return;

    Promise.all([
      fetchApi(`${getApiPath()}/@docs/view?slug=${encodeURIComponent(slug)}`),
      fetch(`${getApiPath()}/@docs/draft?slug=${encodeURIComponent(slug)}`, {
        headers: getAuthHeaders(),
      })
        .then((res) => (res.ok ? res.json() : null))
        .catch(() => null),
    ])
      .then(([docJson, draftJson]) => {
        setDoc(docJson);
        if (draftJson && !draftJson.error) {
          // Resume existing draft
          setDraft(draftJson);
          setTitle(draftJson.title || docJson.title || '');
          setBodyMd(draftJson.body_md || docJson.body_md || '');
          setAuthor(docJson.author || '');
          setVersion(draftJson.version || docJson.version || '');
        } else {
          // No draft — populate from effective document
          setTitle(docJson.title || '');
          setBodyMd(docJson.body_md || '');
          setAuthor(docJson.author || '');
          setVersion(bumpPatch(docJson.version));
        }
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  }, [slug]);

  const handleSaveDraft = async () => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    if (!version) {
      setError('Version is required.');
      setSaving(false);
      return;
    }

    // Validate justification for QMS docs on first save
    if (!draft && isQms) {
      if (!justWhy.trim()) {
        setError('Justification is required for QMS documents. Please fill in the "Why" field.');
        setSaving(false);
        return;
      }
    }

    try {
      const payload = {
        slug,
        version,
        title,
        body_md: bodyMd || null,
      };

      // Include justification on first save of QMS doc
      if (!draft && isQms) {
        payload.justification = {
          what: justWhat,
          why: justWhy,
          scope: justScope,
          classification_estimate: justClassification,
          trigger: 'manual',
        };
      }

      const res = await fetch(`${getApiPath()}/@docs/draft`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(payload),
      });

      if (res.status === 401) {
        window.location.href = `/login?return_url=${encodeURIComponent(window.location.pathname)}`;
        return;
      }
      if (res.status === 403) {
        setError(
          'Permission denied. You need the Manage Documents permission.',
        );
        setSaving(false);
        return;
      }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `${res.status} ${res.statusText}`);
      }

      const draftJson = await res.json();
      setDraft(draftJson);
      setSuccess('Draft saved.');
      setSaving(false);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const handleSubmitForReview = async () => {
    setSubmitModalOpen(false);
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      // Save draft first to capture latest edits
      const saveRes = await fetch(`${getApiPath()}/@docs/draft`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify({
          slug,
          version,
          title,
          body_md: bodyMd || null,
        }),
      });

      if (!saveRes.ok) {
        const body = await saveRes.json().catch(() => ({}));
        throw new Error(body.error || 'Failed to save draft before submit');
      }

      // Build submit payload with evidence for QMS docs
      const submitPayload = { slug };
      if (isQms) {
        submitPayload.coi_declaration = {
          has_conflict: !coiNoConflicts,
          declaration: coiDeclaration,
          micro_enterprise_provision: coiMicroEnterprise,
          same_author_reviewer: coiMicroEnterprise,
          mitigation: coiMicroEnterprise
            ? 'Micro-enterprise provision: same person authors and reviews per SOP-DC-001 §7.2'
            : '',
        };
        submitPayload.qualification = {
          basis: qualBasis,
        };
      }

      // Then submit
      const res = await fetch(`${getApiPath()}/@docs/draft/submit`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          ...getAuthHeaders(),
        },
        body: JSON.stringify(submitPayload),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `${res.status} ${res.statusText}`);
      }

      const draftJson = await res.json();
      setDraft(draftJson);
      setSuccess('Draft submitted for review.');
      setSaving(false);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  const handleAbandon = async () => {
    setAbandonConfirm(false);
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      const res = await fetch(
        `${getApiPath()}/@docs/draft?slug=${encodeURIComponent(slug)}`,
        {
          method: 'DELETE',
          headers: getAuthHeaders(),
        },
      );

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body.error || `${res.status} ${res.statusText}`);
      }

      // Navigate back to document
      history.push(`/docs/${slug}`);
    } catch (err) {
      setError(err.message);
      setSaving(false);
    }
  };

  // Submit for review: for QMS docs, open evidence modal; for non-QMS, use simple confirm
  const handleSubmitClick = () => {
    if (isQms) {
      setSubmitModalOpen(true);
    } else {
      // Non-QMS: direct submit (no evidence required)
      handleSubmitForReview();
    }
  };

  // Can the submit modal be confirmed?
  const canConfirmSubmit =
    !isQms || (coiNoConflicts && qualBasis.trim().length > 0);

  if (loading) {
    return (
      <Container style={{ paddingTop: '3em' }}>
        <Loader active inline="centered" content="Loading document..." />
      </Container>
    );
  }

  const draftStatus = draft
    ? DRAFT_STATUSES[draft.status] || DRAFT_STATUSES.draft
    : null;
  const isReadOnly = draft && draft.status === 'in_review';

  return (
    <Container
      style={{ paddingTop: '2em', paddingBottom: '3em', maxWidth: '900px' }}
    >
      <Helmet title={`Edit — ${title || slug}`} />
      <Link to={`/docs/${slug}`}>&larr; Back to document</Link>

      <Header as="h1" style={{ marginTop: '0.5em' }}>
        <Icon name="edit" /> Edit Document
        <Header.Subheader>{doc?.title || slug}</Header.Subheader>
      </Header>

      {/* Draft status banner */}
      {draft && (
        <Message
          color={draftStatus.color}
          icon={draftStatus.icon}
          header={`Draft Status: ${draftStatus.label}`}
          content={
            draft.status === 'rejected'
              ? `Reviewer feedback: ${draft.reviewer_notes || '(no notes)'}`
              : draft.status === 'in_review'
                ? 'This draft is under review and cannot be edited.'
                : `Editing draft v${draft.version} (effective version: ${doc?.version || '—'})`
          }
        />
      )}

      {!draft && (
        <Message info>
          <Message.Header>Creating a New Draft</Message.Header>
          <p>
            Your changes will be saved as a draft. The current effective version
            (v{doc?.version || '—'}) remains unchanged until the draft is
            approved.
          </p>
        </Message>
      )}

      {error && (
        <Message negative>
          <Message.Header>Error</Message.Header>
          <p>{error}</p>
        </Message>
      )}
      {success && (
        <Message positive>
          <Message.Header>Success</Message.Header>
          <p>{success}</p>
        </Message>
      )}

      <Form>
        {/* CR-2026-026: Justification for QMS documents (new draft only) */}
        {!draft && isQms && (
          <Segment color="blue">
            <Header as="h3">
              <Icon name="file alternate" />
              Change Justification (per WI-DC-001)
            </Header>
            <p style={{ color: '#666', fontSize: '0.9em' }}>
              Required for QMS-controlled documents. Describe what is being
              changed and why.
            </p>
            <Form.Input
              label="What is being changed?"
              value={justWhat}
              onChange={(e) => setJustWhat(e.target.value)}
              placeholder="Section, clause, or document affected"
              required
            />
            <Form.Field required>
              <label>Why is this change needed?</label>
              <TextArea
                value={justWhy}
                onChange={(e) => setJustWhy(e.target.value)}
                placeholder="Business, regulatory, or operational reason for the change"
                rows={3}
              />
            </Form.Field>
            <Form.Input
              label="Scope"
              value={justScope}
              onChange={(e) => setJustScope(e.target.value)}
              placeholder="Which sections or processes are affected"
            />
            <Form.Field>
              <label>Estimated Change Classification</label>
              <Dropdown
                selection
                options={CLASSIFICATION_OPTIONS}
                value={justClassification}
                onChange={(e, { value }) => setJustClassification(value)}
              />
            </Form.Field>
          </Segment>
        )}

        {/* Metadata */}
        <Segment>
          <Header as="h3">Metadata</Header>
          <Grid columns={2} stackable>
            <Grid.Column>
              <Form.Input
                label="Title"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                readOnly={isReadOnly}
              />
              <Form.Input label="Author" value={author} readOnly />
            </Grid.Column>
            <Grid.Column>
              <Form.Input
                label="Version"
                value={version}
                onChange={(e) => setVersion(e.target.value)}
                readOnly={isReadOnly}
                required
                placeholder="e.g. 1.1.0"
              />
              {doc && (
                <Form.Field>
                  <label>Effective Version</label>
                  <Label size="large">{doc.version || '—'}</Label>
                </Form.Field>
              )}
            </Grid.Column>
          </Grid>
        </Segment>

        {/* Body editor */}
        <Segment>
          <Header as="h3">Document Body (Markdown)</Header>
          <textarea
            value={bodyMd}
            onChange={(e) => setBodyMd(e.target.value)}
            rows={40}
            placeholder="Write document content in Markdown..."
            readOnly={isReadOnly}
            style={{
              width: '100%',
              fontFamily: 'monospace',
              fontSize: '0.85em',
              lineHeight: '1.4',
              whiteSpace: 'pre',
              overflowX: 'auto',
              padding: '0.8em',
              border: '1px solid rgba(34,36,38,.15)',
              borderRadius: '4px',
              resize: 'vertical',
            }}
          />
        </Segment>

        {/* Action buttons — emphasis shifts as workflow progresses */}
        <Button.Group>
          <Button
            primary={!draft}
            basic={!!draft}
            loading={saving}
            onClick={handleSaveDraft}
            disabled={isReadOnly}
          >
            <Icon name="save" /> Save Draft
          </Button>
          <Button
            primary={!!draft && !isReadOnly}
            basic={!draft}
            loading={saving}
            onClick={handleSubmitClick}
            disabled={isReadOnly || !draft}
          >
            <Icon name="send" /> Submit for Review
          </Button>
          <Button
            color={draft ? 'red' : undefined}
            basic={!draft}
            loading={saving}
            onClick={() => setAbandonConfirm(true)}
            disabled={!draft}
          >
            <Icon name="trash" /> Abandon Draft
          </Button>
        </Button.Group>

        <Button as={Link} to={`/docs/${slug}`} style={{ marginLeft: '1em' }}>
          Cancel
        </Button>

        {draft && draft.status === 'in_review' && (
          <Button
            as={Link}
            to={`/docs/${slug}/review`}
            color="teal"
            style={{ marginLeft: '0.5em' }}
          >
            <Icon name="eye" /> Go to Review
          </Button>
        )}
      </Form>

      {/* Edit provenance footer */}
      {doc && doc.is_edited && (
        <Segment secondary size="small" style={{ marginTop: '1em' }}>
          <Icon name="info circle" />
          This document has been manually edited and will not be overwritten by
          the git importer.
        </Segment>
      )}

      {/* Confirmation dialogs */}
      <Confirm
        open={abandonConfirm}
        header="Abandon Draft"
        content="Are you sure you want to abandon this draft? All unsaved changes will be lost. The effective version will remain unchanged."
        confirmButton="Abandon Draft"
        cancelButton="Cancel"
        onConfirm={handleAbandon}
        onCancel={() => setAbandonConfirm(false)}
      />

      {/* CR-2026-026: COI + Qualification modal for QMS submit */}
      <Modal
        open={submitModalOpen}
        onClose={() => setSubmitModalOpen(false)}
        size="small"
        closeIcon
      >
        <Modal.Header>
          <Icon name="shield" /> Submit for Review — Evidence Required
        </Modal.Header>
        <Modal.Content>
          <p style={{ color: '#555' }}>
            SOP-DC-001 requires a Conflict of Interest declaration and reviewer
            qualification assessment before submitting for review.
          </p>

          <Segment>
            <Header as="h4">
              <Icon name="handshake" /> COI Declaration
            </Header>
            <Form>
              <Form.Field>
                <Checkbox
                  label="I confirm I have no conflicts of interest regarding this document"
                  checked={coiNoConflicts}
                  onChange={(e, { checked }) => setCoiNoConflicts(checked)}
                />
              </Form.Field>
              <Form.Field>
                <label>Declaration Text</label>
                <TextArea
                  value={coiDeclaration}
                  onChange={(e) => setCoiDeclaration(e.target.value)}
                  rows={2}
                  style={{ fontSize: '0.9em' }}
                />
              </Form.Field>
              <Form.Field>
                <Checkbox
                  label="Micro-enterprise provision: same person authors and reviews (SOP-DC-001 §7.2)"
                  checked={coiMicroEnterprise}
                  onChange={(e, { checked }) => setCoiMicroEnterprise(checked)}
                />
              </Form.Field>
            </Form>
          </Segment>

          <Segment>
            <Header as="h4">
              <Icon name="graduation cap" /> Reviewer Qualification
            </Header>
            <Form>
              <Form.Field required>
                <label>Qualification Basis</label>
                <TextArea
                  value={qualBasis}
                  onChange={(e) => setQualBasis(e.target.value)}
                  rows={3}
                  placeholder="Describe the reviewer's qualifications and domain expertise relevant to this document..."
                />
              </Form.Field>
            </Form>
          </Segment>

          {!canConfirmSubmit && (
            <Message warning size="small">
              <Icon name="exclamation triangle" />
              Please confirm the COI declaration and provide the qualification
              basis to proceed.
            </Message>
          )}
        </Modal.Content>
        <Modal.Actions>
          <Button onClick={() => setSubmitModalOpen(false)}>Cancel</Button>
          <Button
            primary
            disabled={!canConfirmSubmit || saving}
            loading={saving}
            onClick={handleSubmitForReview}
          >
            <Icon name="send" /> Submit for Review
          </Button>
        </Modal.Actions>
      </Modal>
    </Container>
  );
}
